import { FC, useEffect } from 'react';
import useNavigateTo from 'hooks/useNavigateTo';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectBanner } from 'store/pages/pagesSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectPreviewPageMode } from 'store/player/playerSelectors';
import IQuiz from 'models/Quiz';
// Components
import Dialog from 'components/Dialog';
import AppBar from 'components/AppBar';
import PoweredBy from 'components/PoweredBy';
import SkipButton from 'components/SkipButton';
// Icons
import { FiImage } from 'react-icons/fi';

type Props = {
  onClose: () => void;
}

let timeout:any = null;

const ImageDialog:FC<Props> = ({
  // Props
  onClose
}) => {
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const banner = useAppSelector(selectBanner);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const timeoutTime = banner.timeout ? banner.timeout * 1000 : 2000;

  const navigateTo = useNavigateTo();

  useEffect(() => {
    if (!banner.source && !previewPageMode) navigateTo('pages-handler');
    return () => {
      if ( timeout ) clearTimeout(timeout);
    }
    // eslint-disable-next-line
  }, []);

  const handleLoad = () => {
    if (previewPageMode) return;
    if ( timeout ) clearTimeout(timeout);
    timeout = setTimeout(() => {
      onClose();
      navigateTo('pages-handler')
    }, timeoutTime);
  }

  // Broken image link: don't leave the participant stuck, move on right away
  const handleError = () => {
    if (previewPageMode) return;
    if ( timeout ) clearTimeout(timeout);
    onClose();
    navigateTo('pages-handler');
  }

  const handleSkip = () => {
    if ( timeout ) clearTimeout(timeout);
    onClose();
    navigateTo('pages-handler');
  }

  if (!banner.source && !previewPageMode) return null;
  return (
    <Dialog classes="p-4" background={quiz?.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : ''}>
      <AppBar />
      <div className="py-4 relative w-full h-full flex flex-col items-center justify-center gap-6">
        {banner.title ? (
          <h2 className="text-xl font-bold text-primary text-center">{banner.title}</h2>
        ) : null}
        {banner.source ? (
          <img
            className="relative z-10 shadow-primary rounded-lg"
            src={banner.source}
            onLoad={handleLoad}
            onError={handleError}
            alt=""
          />
        ) : (
          <div
            className="relative z-10 rounded-lg flex flex-col items-center justify-center gap-3"
            style={{ width: '100%', aspectRatio: '16/9', background: 'rgba(255,255,255,0.08)', border: '2px dashed rgba(255,255,255,0.2)' }}
          >
            <FiImage size={48} style={{ opacity: 0.3, color: 'white' }} />
            <p className="text-sm font-medium" style={{ opacity: 0.4, color: 'white' }}>Banner image</p>
          </div>
        )}
        {banner.text ? (
          <p className="text-md font-semibold text-primary text-center whitespace-pre-line">{banner.text}</p>
        ) : null}
        {banner.timeout && banner.timeout > 3 ? (
          <SkipButton onSkip={handleSkip} />
        ) : null}
      </div>
      <PoweredBy />
    </Dialog>
  )
}

export default ImageDialog;